import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import $api from 'http/index'
import Notification from 'utils/Notifications'
import { PageHeader, Card, UnvButton, UnvInput, UnvModal, DataTable, Badge, Icon } from 'components'

const DIFFICULTY = [
  { v: 'easy', t: 'Лёгкая' },
  { v: 'medium', t: 'Средняя' },
  { v: 'hard', t: 'Сложная' },
]

const KIND = [
  { v: 'single', t: 'Один правильный' },
  { v: 'multi', t: 'Несколько правильных' },
  { v: 'free', t: 'Свободный ответ' },
]

const emptyOption = () => ({ text: '', is_correct: false })
const emptyForm = () => ({
  text: '',
  kind: 'single',
  difficulty: 'medium',
  points: 1,
  tags: '',
  subject: '',
  options: [emptyOption(), emptyOption()],
})

const QuestionBankPage = () => {
  const { t } = useTranslation('common')

  const [items, setItems] = useState([])
  const [filter, setFilter] = useState({ q: '', subject: '', difficulty: '', tag: '', kind: '' })
  const [subjects, setSubjects] = useState([])
  const [editing, setEditing] = useState(null)
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(emptyForm())
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })
  const [loading, setLoading] = useState(false)
  const fileInput = useRef(null)

  const flash = (m, t = 'success') => setNotify({ isOpen: true, message: m, type: t })

  const load = async () => {
    setLoading(true)
    try {
      const r = await $api.get('/worktests/questions/', { params: filter })
      setItems(r.data?.results || r.data || [])
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  const loadSubjects = async () => {
    try {
      const r = await $api.get('/syllabus/subjects/')
      setSubjects(r.data?.results || r.data || [])
    } catch {
      setSubjects([])
    }
  }

  useEffect(() => {
    loadSubjects()
    load()
  }, [])

  const openEdit = (it) => {
    setEditing(it || null)
    setOpen(true)
    setForm(
      it
        ? {
            text: it.text || '',
            kind: it.kind || 'single',
            difficulty: it.difficulty || 'medium',
            points: it.points ?? 1,
            tags: (it.tags || []).join(', '),
            subject: it.subject || '',
            options: (it.options || []).map((o) => ({
              text: o.text || o,
              is_correct: !!o.is_correct,
            })),
          }
        : emptyForm(),
    )
  }

  const closeEdit = () => {
    setOpen(false)
    setEditing(null)
    setForm(emptyForm())
  }

  const setOpt = (i, k, v) =>
    setForm((f) => ({
      ...f,
      options: f.options.map((o, j) =>
        j === i
          ? { ...o, [k]: v }
          : k === 'is_correct' && f.kind === 'single' && v
          ? { ...o, is_correct: false }
          : o,
      ),
    }))

  const save = async () => {
    const payload = {
      ...form,
      tags: form.tags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      points: Number(form.points) || 1,
      subject: form.subject || null,
    }
    try {
      if (editing) await $api.patch(`/worktests/questions/${editing.id}/`, payload)
      else await $api.post('/worktests/questions/', payload)
      flash('Сохранено')
      closeEdit()
      load()
    } catch {
      flash('Ошибка', 'error')
    }
  }

  const remove = async (id) => {
    if (!window.confirm('Удалить вопрос?')) return
    try {
      await $api.delete(`/worktests/questions/${id}/`)
      flash('Удалено')
      load()
    } catch {
      flash('Ошибка', 'error')
    }
  }

  const onPickFile = () => fileInput.current?.click()

  const onFileChange = async (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    const data = new FormData()
    data.append('file', f)
    try {
      const r = await $api.post('/worktests/questions/import-csv/', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const created = r.data?.created ?? 0
      flash(`Импортировано: ${created}`)
      load()
    } catch {
      flash('Ошибка импорта', 'error')
    } finally {
      e.target.value = ''
    }
  }

  return (
    <div style={{ margin: '0 auto' }}>
      <PageHeader
        icon={<Icon name="folder" size={28} />}
        title={t('menu.questionBank')}
        subtitle={t('auto.questionbank.subtitle', 'База вопросов для тестирования')}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <UnvButton variant="primary" iconLeft="plus" onClick={() => openEdit(null)}>{t('auto.questionbank.new', 'Новый вопрос')}</UnvButton>
            <UnvButton variant="secondary" iconLeft="upload" onClick={onPickFile}>{t('tests.importCsv')}</UnvButton>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.xlsx,.xlsm"
              style={{ display: 'none' }}
              onChange={onFileChange}
            />
          </div>
        }
      />

      <Card padding="md" style={{ marginBottom: 16 }}>
        <Card.Body>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr) auto', gap: 8 }}>
          <input
            className="unv-input"
            placeholder={t('common.search')}
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && load()}
          />
          <select
            className="unv-select"
            value={filter.subject}
            onChange={(e) => setFilter({ ...filter, subject: e.target.value })}
          >
            <option value="">{t('auto.testpage_questionbankpage.k1')}</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name_subject || s.name}
              </option>
            ))}
          </select>
          <select
            className="unv-select"
            value={filter.difficulty}
            onChange={(e) => setFilter({ ...filter, difficulty: e.target.value })}
          >
            <option value="">{t('auto.testpage_questionbankpage.k2')}</option>
            {DIFFICULTY.map((d) => (
              <option key={d.v} value={d.v}>
                {d.t}
              </option>
            ))}
          </select>
          <select
            className="unv-select"
            value={filter.kind}
            onChange={(e) => setFilter({ ...filter, kind: e.target.value })}
          >
            <option value="">{t('library.all_kinds')}</option>
            {KIND.map((k) => (
              <option key={k.v} value={k.v}>
                {k.t}
              </option>
            ))}
          </select>
          <input
            className="unv-input"
            placeholder={t('auto.testpage_questionbankpage.k5')}
            value={filter.tag}
            onChange={(e) => setFilter({ ...filter, tag: e.target.value })}
          />
          <UnvButton variant="secondary" iconLeft="filter" onClick={load}>{t('common.apply')}</UnvButton>
        </div>
        </Card.Body>
      </Card>

      <Card padding="none">
        <Card.Body>
          <DataTable
            loading={loading}
            data={items}
            searchable={false}
            emptyText={t('auto.testpage_questionbankpage.k3')}
            columns={[
              { key: 'text', label: t('survey.question') },
              { key: 'kind', label: t('schedule.type'), render: (v) => <Badge variant="info">{KIND.find((k) => k.v === v)?.t || v}</Badge> },
              { key: 'difficulty', label: t('tests.difficulty'), render: (v) => DIFFICULTY.find((d) => d.v === v)?.t || v || '—' },
              { key: 'points', label: t('journal.points') },
              { key: 'tags', label: t('tests.tags'), render: (v) => (
                <span>{(v || []).map((tag, i) => <Badge key={i} variant="muted" className="" >{tag}</Badge>)}</span>
              ) },
              { key: 'actions', label: '', render: (_, row) => (
                <div style={{ display: 'flex', gap: 6 }}>
                  <UnvButton variant="ghost" size="sm" iconLeft="edit" onClick={() => openEdit(row)}>{t('common.edit', 'Изменить')}</UnvButton>
                  <UnvButton variant="danger" size="sm" iconLeft="delete" onClick={() => remove(row.id)}>{t('common.delete', 'Удалить')}</UnvButton>
                </div>
              ) },
            ]}
          />
        </Card.Body>
      </Card>

      <UnvModal
        open={open}
        onClose={closeEdit}
        title={editing ? t('auto.questionbank.edit', 'Редактирование вопроса') : t('auto.questionbank.new', 'Новый вопрос')}
        size="lg"
      >
        <div style={{ display: 'grid', gap: 12 }}>
              <textarea
                className="unv-textarea"
                placeholder={t('surveyForm.question_text')}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
              />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <select
                  className="unv-select"
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                >
                  {KIND.map((k) => (
                    <option key={k.v} value={k.v}>
                      {k.t}
                    </option>
                  ))}
                </select>
                <select
                  className="unv-select"
                  value={form.difficulty}
                  onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                >
                  {DIFFICULTY.map((d) => (
                    <option key={d.v} value={d.v}>
                      {d.t}
                    </option>
                  ))}
                </select>
                <input
                  className="unv-input"
                  type="number"
                  min={0}
                  value={form.points}
                  onChange={(e) => setForm({ ...form, points: e.target.value })}
                  placeholder={t('journal.points')}
                />
                <select
                  className="unv-select"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                >
                  <option value="">{t('auto.testpage_questionbankpage.k4')}</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name_subject || s.name}
                    </option>
                  ))}
                </select>
              </div>
              <input
                className="unv-input"
                placeholder={t('auto.testpage_questionbankpage.k6')}
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
              />
              {form.kind !== 'free' && (
                <div>
                  {form.options.map((o, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 6,
                      }}
                    >
                      <input
                        type={form.kind === 'single' ? 'radio' : 'checkbox'}
                        name="bank-correct"
                        checked={!!o.is_correct}
                        onChange={(e) => setOpt(i, 'is_correct', e.target.checked)}
                      />
                      <input
                        className="unv-input"
                        placeholder={`Вариант ${i + 1}`}
                        value={o.text}
                        onChange={(e) => setOpt(i, 'text', e.target.value)}
                      />
                      <button
                        className="unv-btn unv-btn--ghost unv-btn--sm"
                        onClick={() =>
                          setForm({
                            ...form,
                            options: form.options.filter((_, j) => j !== i),
                          })
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    className="unv-btn unv-btn--ghost unv-btn--sm"
                    onClick={() =>
                      setForm({ ...form, options: [...form.options, emptyOption()] })
                    }
                  >
                    + Добавить вариант
                  </button>
                </div>
              )}
        </div>
        <UnvModal.Footer>
          <UnvButton variant="ghost" onClick={closeEdit}>{t('common.cancel')}</UnvButton>
          <UnvButton variant="primary" iconLeft="save" onClick={save}>{t('common.save')}</UnvButton>
        </UnvModal.Footer>
      </UnvModal>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default QuestionBankPage
